import { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Share2, CheckCircle2, Clock, ExternalLink,
  Settings, FileSpreadsheet, FileCode, Car, AlertCircle,
  Upload, Power, RefreshCw, Server, ShieldCheck, XCircle, AlertTriangle, Info
} from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const platforms = [
  { id: 'autotrader', name: 'AutoTrader.ca', logo: '🚗', description: 'Le plus grand site automobile au Canada', color: 'bg-red-500', hasExport: true, hasFtp: true },
  { id: 'facebook', name: 'Facebook Marketplace', logo: '📘', description: 'Géré par extension externe', color: 'bg-blue-600', hasExport: false, externalNote: 'Géré par extension externe' },
  { id: 'kijiji', name: 'Kijiji Autos', logo: '🟣', description: 'Petites annonces populaires', color: 'bg-purple-600', hasExport: true, comingSoon: true },
  { id: 'cargurus', name: 'CarGurus', logo: '🔵', description: 'Comparateur de prix', color: 'bg-cyan-600', hasExport: true, comingSoon: true },
];

const AdminExports = () => {
  const { getAuthHeader } = useAuth();
  const [settings, setSettings] = useState({ autotrader_dealer_id: '' });
  const [exporting, setExporting] = useState(null);
  const [vehicleCount, setVehicleCount] = useState(0);
  const [lastExport, setLastExport] = useState(null);
  const [showSettings, setShowSettings] = useState(null);
  const [ftpStatus, setFtpStatus] = useState(null);
  const [ftpEnabled, setFtpEnabled] = useState(true);
  const [uploadingFtp, setUploadingFtp] = useState(false);
  const [verifying1Source, setVerifying1Source] = useState(false);
  const [verificationResult, setVerificationResult] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchVehicleCount();
    fetchFtpStatus();
    fetch1SourceStatus();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/exports`, { headers: getAuthHeader() });
      if (response.data) {
        setSettings(response.data);
        setLastExport(response.data.last_export);
        setFtpEnabled(response.data.ftp_enabled !== false);
      }
    } catch (error) {
      console.error('Error fetching export settings:', error);
    }
  };

  const fetchVehicleCount = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/stats`, { headers: getAuthHeader() });
      setVehicleCount(response.data.available || 0);
    } catch (error) {
      console.error('Error fetching vehicle count:', error);
    }
  };

  const fetchFtpStatus = async () => {
    try {
      const response = await axios.get(`${API}/exports/autotrader/ftp/status`, { headers: getAuthHeader() });
      setFtpStatus(response.data);
    } catch (error) {
      console.error('Error fetching FTP status:', error);
    }
  };

  const saveSettings = async () => {
    try {
      await axios.put(`${API}/settings/exports`, settings, { headers: getAuthHeader() });
      toast.success('Paramètres sauvegardés!');
      setShowSettings(null);
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const toggleFtpEnabled = async () => {
    try {
      const newValue = !ftpEnabled;
      await axios.put(`${API}/settings/exports`, { ...settings, ftp_enabled: newValue }, { headers: getAuthHeader() });
      setFtpEnabled(newValue);
      toast.success(newValue ? 'Export FTP activé' : 'Export FTP désactivé');
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const triggerFtpUpload = async () => {
    setUploadingFtp(true);
    try {
      const response = await axios.post(`${API}/exports/autotrader/ftp`, {}, { headers: getAuthHeader() });
      if (response.data.success) {
        toast.success(`${response.data.vehicles_exported} véhicules exportés vers AutoTrader!`);
        fetchFtpStatus();
      } else {
        toast.error(`Erreur: ${response.data.error}`);
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi FTP");
    } finally {
      setUploadingFtp(false);
    }
  };

  const downloadFtpPreview = async () => {
    try {
      const response = await axios.get(`${API}/exports/autotrader/preview`, { headers: getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `autotrader_preview_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Aperçu CSV téléchargé!');
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const exportFeed = async (platform, format) => {
    setExporting(`${platform}-${format}`);
    try {
      const response = await axios.get(`${API}/exports/${platform}?format=${format}`, { headers: getAuthHeader(), responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${platform}_inventory_${new Date().toISOString().split('T')[0]}.${format}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} téléchargé!`);
      await axios.post(`${API}/exports/log`, { platform, format }, { headers: getAuthHeader() });
      fetchSettings();
    } catch (error) {
      toast.error("Erreur lors de l'export");
    } finally {
      setExporting(null);
    }
  };

  const fetch1SourceStatus = async () => {
    try {
      const response = await axios.get(`${API}/exports/autotrader/verify-1source/status`, { headers: getAuthHeader() });
      if (response.data?.result) {
        setVerificationResult(response.data.result);
      }
    } catch (error) {
      console.error('Error fetching 1Source status:', error);
    }
  };

  const trigger1SourceVerification = async () => {
    setVerifying1Source(true);
    try {
      const response = await axios.post(`${API}/exports/autotrader/verify-1source`, {}, { headers: getAuthHeader(), timeout: 120000 });
      setVerificationResult(response.data);
      if (response.data.critical_count > 0) {
        toast.error(`${response.data.critical_count} erreur(s) critique(s) trouvée(s)!`);
      } else if (response.data.all_match) {
        toast.success('Tout est correct! Aucune erreur trouvée.');
      } else {
        toast.info(`Vérification terminée: ${response.data.total_mismatches} différence(s)`);
      }
    } catch (error) {
      toast.error("Erreur lors de la vérification 1Source");
    } finally {
      setVerifying1Source(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Jamais';
    return new Date(dateStr).toLocaleString('fr-CA', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <AdminLayout>
      <div className="space-y-4" data-testid="admin-exports">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-lg font-bold text-slate-800 flex items-center gap-2">
              <Share2 className="h-5 w-5 text-green-600" />
              Plateformes externes
            </h1>
            <p className="text-slate-500 text-xs">Exportez votre inventaire</p>
          </div>
          <Badge variant="outline" className="border-green-200 text-green-700 bg-green-50 text-[10px]">
            <Car className="h-3 w-3 mr-1" />
            {vehicleCount} véhicules
          </Badge>
        </div>

        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
          <p className="text-blue-700 text-[11px] flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Seuls les véhicules "Disponible" avec prix sont exportés.
          </p>
        </div>

        {/* AutoTrader FTP Section */}
        {ftpStatus?.configured && (
          <div className="bg-white border-2 border-red-200 rounded-lg overflow-hidden">
            <div className="bg-red-500 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Server className="h-5 w-5 text-white" />
                  <div>
                    <h3 className="text-white font-bold text-sm">AutoTrader FTP Automatique</h3>
                    <p className="text-white/80 text-[10px]">Envoi automatique quotidien à 6h00</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-white text-xs">{ftpEnabled ? 'Activé' : 'Désactivé'}</span>
                  <Switch 
                    checked={ftpEnabled} 
                    onCheckedChange={toggleFtpEnabled}
                    className="data-[state=checked]:bg-green-500"
                  />
                </div>
              </div>
            </div>
            
            <div className="p-4 space-y-4">
              {/* FTP Status Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[10px] text-slate-400">Serveur FTP</p>
                  <p className="text-xs font-medium text-slate-700">{ftpStatus.ftp_host}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[10px] text-slate-400">Fichier</p>
                  <p className="text-xs font-medium text-slate-700">{ftpStatus.ftp_filename}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[10px] text-slate-400">Dernier envoi</p>
                  <p className="text-xs font-medium text-slate-700">{formatDate(ftpStatus.last_export)}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[10px] text-slate-400">Véhicules envoyés</p>
                  <p className="text-xs font-medium text-slate-700">{ftpStatus.last_export_count || 0}</p>
                </div>
              </div>

              {/* Next Scheduled */}
              {ftpStatus.next_scheduled_export && ftpEnabled && (
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <Clock className="h-3 w-3" />
                  <span>Prochain envoi: {formatDate(ftpStatus.next_scheduled_export)}</span>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2">
                <Button 
                  onClick={triggerFtpUpload} 
                  disabled={uploadingFtp}
                  className="bg-red-600 hover:bg-red-700 text-xs h-8"
                >
                  {uploadingFtp ? (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="h-3 w-3 mr-1" />
                      Envoyer maintenant
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={downloadFtpPreview}
                  className="text-xs h-8"
                >
                  <FileSpreadsheet className="h-3 w-3 mr-1" />
                  Télécharger aperçu CSV
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* 1Source Verification Section */}
        <div className="bg-white border-2 border-emerald-200 rounded-lg overflow-hidden" data-testid="1source-verification">
          <div className="bg-emerald-600 p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <ShieldCheck className="h-5 w-5 text-white" />
                <div>
                  <h3 className="text-white font-bold text-sm">Vérification 1Source AutoTrader</h3>
                  <p className="text-white/80 text-[10px]">Compare les annonces LIVE avec notre inventaire</p>
                </div>
              </div>
              {verificationResult && (
                <Badge className={`text-[10px] ${verificationResult.all_match ? 'bg-green-100 text-green-800' : verificationResult.critical_count > 0 ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}`}>
                  {verificationResult.all_match ? 'Tout correct' : `${verificationResult.critical_count} erreur(s)`}
                </Badge>
              )}
            </div>
          </div>

          <div className="p-4 space-y-4">
            {/* Stats Row */}
            {verificationResult && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[10px] text-slate-400">Notre CSV</p>
                  <p className="text-xs font-medium text-slate-700">{verificationResult.our_csv_count} véhicules</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[10px] text-slate-400">LIVE AutoTrader</p>
                  <p className="text-xs font-medium text-slate-700">{verificationResult.live_count} véhicules</p>
                </div>
                <div className={`rounded-lg p-2 ${verificationResult.critical_count > 0 ? 'bg-red-50' : 'bg-green-50'}`}>
                  <p className="text-[10px] text-slate-400">Critiques</p>
                  <p className={`text-xs font-bold ${verificationResult.critical_count > 0 ? 'text-red-600' : 'text-green-600'}`}>{verificationResult.critical_count}</p>
                </div>
                <div className={`rounded-lg p-2 ${verificationResult.warning_count > 0 ? 'bg-yellow-50' : 'bg-green-50'}`}>
                  <p className="text-[10px] text-slate-400">Avertissements</p>
                  <p className={`text-xs font-medium ${verificationResult.warning_count > 0 ? 'text-yellow-600' : 'text-green-600'}`}>{verificationResult.warning_count}</p>
                </div>
                <div className="bg-slate-50 rounded-lg p-2">
                  <p className="text-[10px] text-slate-400">Dernière vérification</p>
                  <p className="text-xs font-medium text-slate-700">{formatDate(verificationResult.timestamp)}</p>
                </div>
              </div>
            )}

            {/* All Match Banner */}
            {verificationResult?.all_match && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                <p className="text-green-700 text-xs font-medium">Tout est correct! Les prix et stocks sur AutoTrader correspondent exactement à notre inventaire.</p>
              </div>
            )}

            {/* Mismatches Table */}
            {verificationResult?.mismatches?.length > 0 && !verificationResult.all_match && (
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-100">
                    <tr>
                      <th className="text-left p-2 text-slate-500 font-medium">Sévérité</th>
                      <th className="text-left p-2 text-slate-500 font-medium">Type</th>
                      <th className="text-left p-2 text-slate-500 font-medium">Stock</th>
                      <th className="text-left p-2 text-slate-500 font-medium">Véhicule</th>
                      <th className="text-left p-2 text-slate-500 font-medium">Détail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {verificationResult.mismatches.map((m, i) => (
                      <tr key={i} className={`border-t border-slate-100 ${m.severity === 'CRITICAL' ? 'bg-red-50/50' : m.severity === 'WARNING' ? 'bg-yellow-50/50' : ''}`}>
                        <td className="p-2">
                          <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${m.severity === 'CRITICAL' ? 'text-red-600' : m.severity === 'WARNING' ? 'text-yellow-600' : 'text-slate-400'}`}>
                            {m.severity === 'CRITICAL' ? <XCircle className="h-3 w-3" /> : m.severity === 'WARNING' ? <AlertTriangle className="h-3 w-3" /> : <Info className="h-3 w-3" />}
                            {m.severity}
                          </span>
                        </td>
                        <td className="p-2 text-slate-600">{m.type === 'PRICE_MISMATCH' ? 'Prix' : m.type === 'STOCK_MISMATCH' ? 'Stock' : m.type === 'NOT_IN_OUR_EXPORT' ? 'Non exporté' : 'Non LIVE'}</td>
                        <td className="p-2 font-mono text-slate-700">{m.stock}</td>
                        <td className="p-2 text-slate-700">{m.vehicle}</td>
                        <td className="p-2 text-slate-500">{m.detail}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* No result yet */}
            {!verificationResult && (
              <div className="text-center py-4">
                <ShieldCheck className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                <p className="text-slate-400 text-xs">Aucune vérification effectuée. Cliquez pour vérifier.</p>
              </div>
            )}

            {/* Action Button */}
            <Button 
              onClick={trigger1SourceVerification} 
              disabled={verifying1Source}
              className="bg-emerald-600 hover:bg-emerald-700 text-xs h-8"
              data-testid="verify-1source-btn"
            >
              {verifying1Source ? (
                <>
                  <RefreshCw className="h-3 w-3 mr-1 animate-spin" />
                  Vérification en cours... (~15s)
                </>
              ) : (
                <>
                  <ShieldCheck className="h-3 w-3 mr-1" />
                  Vérifier maintenant
                </>
              )}
            </Button>

            <p className="text-[10px] text-slate-400">
              La vérification s'exécute automatiquement après chaque envoi FTP. Alerte par email en cas d'erreur.
            </p>
          </div>
        </div>

        {/* Platform Cards */}
        <div className="grid md:grid-cols-2 gap-4">
          {platforms.map((platform) => (
            <div key={platform.id} className="bg-white border border-slate-200 rounded-lg overflow-hidden">
              {/* Platform Header */}
              <div className={`${platform.color} p-3`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-2xl">{platform.logo}</span>
                    <div>
                      <h3 className="text-white font-bold text-sm">{platform.name}</h3>
                      <p className="text-white/80 text-[10px]">{platform.description}</p>
                    </div>
                  </div>
                  {platform.comingSoon && <Badge className="bg-white/20 text-white text-[9px]">Bientôt</Badge>}
                </div>
              </div>

              {/* Platform Body */}
              <div className="p-3">
                {platform.externalNote ? (
                  <div className="text-center py-3">
                    <CheckCircle2 className="h-8 w-8 text-green-500 mx-auto mb-2" />
                    <p className="text-slate-600 text-xs">{platform.externalNote}</p>
                  </div>
                ) : platform.comingSoon ? (
                  <div className="text-center py-3">
                    <Clock className="h-8 w-8 text-slate-300 mx-auto mb-2" />
                    <p className="text-slate-400 text-xs">En développement</p>
                  </div>
                ) : (
                  <>
                    {showSettings === platform.id && (
                      <div className="mb-3 p-3 bg-slate-50 rounded-lg space-y-2">
                        <div>
                          <Label className="text-slate-600 text-[10px]">Dealer ID</Label>
                          <Input value={settings.autotrader_dealer_id || ''} onChange={(e) => setSettings({...settings, autotrader_dealer_id: e.target.value})} placeholder="Votre ID" className="mt-1 text-xs h-7" />
                        </div>
                        <div className="flex gap-1">
                          <Button size="sm" onClick={saveSettings} className="text-[10px] h-6">Sauvegarder</Button>
                          <Button size="sm" variant="outline" onClick={() => setShowSettings(null)} className="text-[10px] h-6">Annuler</Button>
                        </div>
                      </div>
                    )}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="text-slate-400">Dernier export manuel:</span>
                        <span className="text-slate-600">{formatDate(lastExport?.[platform.id])}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Button onClick={() => exportFeed(platform.id, 'csv')} disabled={exporting === `${platform.id}-csv`} size="sm" className="bg-slate-700 hover:bg-slate-600 text-[10px] h-7">
                          <FileSpreadsheet className="h-3 w-3 mr-1" />
                          {exporting === `${platform.id}-csv` ? '...' : 'CSV'}
                        </Button>
                        <Button onClick={() => exportFeed(platform.id, 'xml')} disabled={exporting === `${platform.id}-xml`} size="sm" className="bg-slate-700 hover:bg-slate-600 text-[10px] h-7">
                          <FileCode className="h-3 w-3 mr-1" />
                          {exporting === `${platform.id}-xml` ? '...' : 'XML'}
                        </Button>
                      </div>
                      <div className="flex items-center justify-between pt-1">
                        <Button size="sm" variant="ghost" onClick={() => setShowSettings(showSettings === platform.id ? null : platform.id)} className="text-slate-400 hover:text-slate-700 text-[10px] h-6 px-2">
                          <Settings className="h-3 w-3 mr-1" />
                          Paramètres
                        </Button>
                        <a href="https://www.autotrader.ca/dealers" target="_blank" rel="noopener noreferrer" className="text-[10px] text-blue-600 hover:underline flex items-center gap-0.5">
                          Portail <ExternalLink className="h-2.5 w-2.5" />
                        </a>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminExports;
